import * as React from 'react';

import {
  AsyncBoundary,
  Col,
  ConsoleDialog,
  PhotoPreviewDialog,
} from '@rematter/ui-web';
import { useAtomValue } from 'jotai';

import * as state from '../../_state/transactions.state';
import { usePhotoPreview } from '../../hooks/usePhotoPreview';
import { useTranslations } from '../../i18n/useTranslations';
import { GenericFile } from '../../types/file';
import { ScaleTicketPhotoGrid } from '../scale-ticket-pro/shared/ScaleTicketPhotoGrid';

import { useFileDownload } from '~/shared/hooks/useFileDownload';
import { UploadErrorActions } from '~/shared/types/file';

type TransactionPhotosDialogProps = {
  documentNumber: string;
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
};

const noop = () => null;

const noopUploadErrorActions: UploadErrorActions = {
  retryUpload: noop,
  dismissError: noop,
};

//
// ⚛️ ⚛️ ⚛️ TRANSACTION PHOTOS DIALOG ⚛️ ⚛️ ⚛️
//
export const TransactionPhotosDialog = ({
  documentNumber,
  isOpen,
  onOpenChange,
}: TransactionPhotosDialogProps) => {
  const t = useTranslations();

  // Atoms
  const photosResult = useAtomValue(state.$selectedTransactionPhotos);

  // Data hooks
  const { downloadFile, isGeneratingDownloadUrl } = useFileDownload();

  // Memoized values
  const photoFiles = React.useMemo<GenericFile[]>(() => {
    if (!photosResult?.data) return [];
    return photosResult.data
      .filter((photo) => photo.mimeType.startsWith('image/'))
      .map((photo) => ({
        id: photo.id,
        fileName: photo.fileName,
        fileSize: photo.fileSize,
        mimeType: photo.mimeType,
        status: photo.status,
        downloadUrl: photo.url,
        metadata: photo.metadata || {},
        createdAt: photo.createdAt?.toString(),
        updatedAt: photo.updatedAt?.toString(),
      }));
  }, [photosResult?.data]);

  const {
    previewOpen,
    currentPhotoIndex,
    previewPhotos,
    handlePhotoView,
    closePreview,
    navigatePreview,
  } = usePhotoPreview(photoFiles);

  return (
    <>
      <ConsoleDialog
        title={t.transactionsView.actions.ticketPhotosTitle(documentNumber)}
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        size="fill"
        content={
          <AsyncBoundary
            loading={photosResult.isFetching}
            error={photosResult.error}
          >
            <Col gap4 className="@TransactionPhotosDialog">
              {photoFiles.length > 0 ? (
                <ScaleTicketPhotoGrid
                  files={photoFiles}
                  uploadErrorActions={noopUploadErrorActions}
                  downloadFile={downloadFile}
                  isGeneratingDownloadUrl={isGeneratingDownloadUrl}
                  onDeletePhoto={noop}
                  onRetryDelete={noop}
                  onDismissOptimisticError={noop}
                  onView={handlePhotoView}
                />
              ) : (
                <p className="py-8 text-center text-sm text-neutral/50">
                  {t.transactionsView.actions.noPhotos}
                </p>
              )}
            </Col>
          </AsyncBoundary>
        }
      />
      <PhotoPreviewDialog
        albumName={t.transactionsView.actions.ticketPhotosTitle(documentNumber)}
        photos={previewPhotos}
        currentIndex={currentPhotoIndex}
        isOpen={previewOpen}
        onClose={closePreview}
        onNavigate={navigatePreview}
      />
    </>
  );
};
